import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectDatabaseCredentials } from '@claude-code-api/shared';
import {
  Badge,
  Button,
  Label,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui';
import { toast } from 'sonner';
import {
  Table as TableIcon,
  Key,
  RefreshCw,
  Check,
  Copy,
  Eye,
  EyeOff,
} from 'lucide-react';
import { DatabaseTableBrowser } from './DatabaseTableBrowser';
import type { DatabaseInfo } from './DatabaseList';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getApiBase(db: DatabaseInfo): string {
  return db.type === 'standalone'
    ? `/api/databases/${db.id}`
    : `/api/projects/${db.projectId}/database`;
}

interface DatabaseDetailProps {
  database: DatabaseInfo;
  onUpdateDatabase: (id: string, updates: Partial<DatabaseInfo>) => void;
}

export function DatabaseDetail({ database: db, onUpdateDatabase }: DatabaseDetailProps) {
  const { t } = useTranslation('databases');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState<string | null>(null);
  const attemptedInitialTablesLoadRef = useRef(false);
  const attemptedInitialCredentialsLoadRef = useRef(false);

  const apiBase = getApiBase(db);

  useEffect(() => {
    attemptedInitialTablesLoadRef.current = false;
    attemptedInitialCredentialsLoadRef.current = false;
  }, [db.id]);

  const fetchCredentials = useCallback(async () => {
    attemptedInitialCredentialsLoadRef.current = true;
    if (db.credentials) return;
    onUpdateDatabase(db.id, { loadingCredentials: true });
    try {
      const response = await fetch(`${apiBase}/credentials`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        onUpdateDatabase(db.id, { credentials: data.data, loadingCredentials: false });
      } else {
        onUpdateDatabase(db.id, { loadingCredentials: false });
      }
    } catch {
      toast.error(t('errors.loadCredentials'));
      onUpdateDatabase(db.id, { loadingCredentials: false });
    }
  }, [db.id, db.credentials, apiBase, onUpdateDatabase, t]);

  const fetchTables = useCallback(async () => {
    attemptedInitialTablesLoadRef.current = true;
    onUpdateDatabase(db.id, { loadingTables: true });
    try {
      const response = await fetch(`${apiBase}/tables`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        onUpdateDatabase(db.id, { tables: data.data, loadingTables: false });
      } else {
        onUpdateDatabase(db.id, { loadingTables: false });
      }
    } catch {
      toast.error(t('errors.loadTables'));
      onUpdateDatabase(db.id, { loadingTables: false });
    }
  }, [db.id, apiBase, onUpdateDatabase, t]);

  const handleTabChange = (tab: string) => {
    if (tab === 'connection' && !db.credentials && !db.loadingCredentials) {
      fetchCredentials();
    } else if (tab === 'tables' && db.tables.length === 0 && !db.loadingTables) {
      fetchTables();
    }
  };

  useEffect(() => {
    if (!attemptedInitialTablesLoadRef.current && db.tables.length === 0 && !db.loadingTables) {
      fetchTables();
    }
    if (!attemptedInitialCredentialsLoadRef.current && !db.credentials && !db.loadingCredentials) {
      fetchCredentials();
    }
  }, [db.tables.length, db.loadingTables, db.credentials, db.loadingCredentials, fetchTables, fetchCredentials]);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // silent
    }
  };

  const renderCredentials = (credentials: ProjectDatabaseCredentials) => (
    <div className="space-y-4">
      <div>
        <Label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">
          {t('connection.url')}
        </Label>
        <div className="flex items-center gap-2">
          <div className="flex-1 px-4 py-3 bg-muted border rounded-lg font-mono text-[13px] truncate">
            {credentials.databaseUrl}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyToClipboard(credentials.databaseUrl, `url-${db.id}`)}
            aria-label={t('connection.copyToClipboard')}
          >
            {copiedField === `url-${db.id}` ? (
              <><Check className="w-4 h-4 mr-1" /> {t('actions.copied')}</>
            ) : (
              <><Copy className="w-4 h-4 mr-1" /> {t('actions.copy')}</>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: 'host', label: t('connection.host'), value: credentials.host },
          { key: 'port', label: t('connection.port'), value: credentials.port },
          { key: 'database', label: t('connection.database'), value: credentials.database },
          { key: 'username', label: t('connection.username'), value: credentials.username },
        ].map((item) => (
          <div key={item.key} className="bg-muted rounded-md p-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{item.label}</p>
            <div className="flex items-center justify-between">
              <code className="text-[13px] font-mono truncate">{item.value}</code>
              <button
                onClick={() => copyToClipboard(item.value, `${item.key}-${db.id}`)}
                className="ml-2 p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                aria-label={t('connection.copyToClipboard')}
              >
                {copiedField === `${item.key}-${db.id}` ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        ))}
        <div className="bg-muted rounded-md p-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t('connection.password')}</p>
          <div className="flex items-center justify-between">
            <code className="text-[13px] font-mono truncate">
              {showPassword ? credentials.password : t('connection.hiddenPassword')}
            </code>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                aria-label={showPassword ? t('connection.hidePassword') : t('connection.showPassword')}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => copyToClipboard(credentials.password, `password-${db.id}`)}
                className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                aria-label={t('connection.copyToClipboard')}
              >
                {copiedField === `password-${db.id}` ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Tabs defaultValue="tables" onValueChange={handleTabChange} className="w-full">
      <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto">
        <TabsTrigger
          value="connection"
          className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 px-4"
        >
          <Key className="w-4 h-4 mr-2" />
          {t('connection.tab')}
        </TabsTrigger>
        <TabsTrigger
          value="tables"
          className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 px-4"
        >
          <TableIcon className="w-4 h-4 mr-2" />
          {t('tables.tab')}
          {db.tables.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {db.tables.length}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="connection" className="p-6 mt-0">
        {db.loadingCredentials ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-6 w-6" />
            <span className="ml-3 text-sm text-muted-foreground">{t('loading.credentials')}</span>
          </div>
        ) : db.credentials ? (
          renderCredentials(db.credentials)
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">{t('empty.credentials')}</p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="tables" className="p-6 mt-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchTables}
              disabled={db.loadingTables}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${db.loadingTables ? 'animate-spin' : ''}`} />
              {t('actions.refresh')}
            </Button>
          </div>
        </div>

        {db.loadingTables && db.tables.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-6 w-6" />
            <span className="ml-3 text-sm text-muted-foreground">{t('loading.tables')}</span>
          </div>
        ) : db.tables.length === 0 ? (
          <div className="text-center py-8 bg-muted rounded-lg">
            <TableIcon className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">{t('empty.noTables')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {db.tables.map((table) => (
                <button
                  key={table.name}
                  onClick={() => setSelectedTableName(
                    selectedTableName === table.name ? null : table.name
                  )}
                  className={`p-4 text-left rounded-lg border-2 transition-all duration-150 ${
                    selectedTableName === table.name
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/30 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <TableIcon className="w-4 h-4 text-primary" />
                    <span className="font-medium truncate">{table.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t('tables.columnCount', { count: table.columnCount })}</span>
                    <span>{formatBytes(table.sizeBytes)}</span>
                  </div>
                </button>
              ))}
            </div>

            {selectedTableName && (
              <DatabaseTableBrowser
                databaseId={db.id}
                tableName={selectedTableName}
                apiBasePath={apiBase}
              />
            )}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
